export type ReturnLineSelection = { receiptLineId: string; quantity: string };
export type ReceiptReturnLine = {
  id: string; itemId: string; itemName: string; unit: string; receivedQty: string;
  returnedQty: string; reservedQty: string; availableQty: string; unitCost: string; lineValue: string;
};
export type SupplierReceiptReturn = {
  id: string; returnNumber: string; status: string; totalValue: string;
  settlementType: "credit" | "cash"; settledAmount: string; createdAt: string;
  receiptId: string; receiptNumber: string; supplierName?: string; branchName?: string; branchId?: string;
  lines: { supplierReceiptLineId: string; quantity: string; unitCost: string; value: string }[];
};
export type ReceiptReturns = {
  canReturn: boolean; blockedCode?: "missing_permission" | "not_confirmed" | "on_hold"; blockedReason?: string; approvalRequired: boolean; cashAvailable: boolean;
  receiptId: string; receiptNumber: string; lines: ReceiptReturnLine[];
  returns: SupplierReceiptReturn[]; fullyReturned: boolean;
};
export type ReturnPreview = {
  token: string; totalValue: string; approvalRequired: boolean; cashAvailable: boolean; cashRefundAmount: string;
  lines: { receiptLineId: string; itemName: string; unit: string; quantity: string; unitCost: string; value: string; currentQuantity: string; afterQuantity: string; shortage: string | boolean }[];
};
export type ReturnReceiptSource = {
  receiptId: string; receiptNumber: string; settlementType: "credit" | "cash"; settledAmount: string;
  signedPaperUrl?: string; stockPosted: boolean; shortages: unknown;
  lines: { supplierReceiptLineId: string; quantity: string; unitCost: string; value: string }[];
  shipPreview?: { lines: { itemName: string; currentQuantity: string; quantity: string; afterQuantity: string; shortage: string | boolean }[]; requiresAcknowledgement: boolean };
};
export type CreateReceiptReturn = {
  workspaceId: string; operationKey: string; previewToken: string; lines: ReturnLineSelection[];
  reason: string; photoDataUrl: string; settlementType: "credit" | "cash"; cashReturned: boolean; acknowledgeShortage: boolean;
};
// Decimal comparison without converting receipt quantities to floating-point.
export function compareReturnDecimal(a: string, b: string): number | null {
  const valid = /^\d+(?:\.\d+)?$/;
  if (!valid.test(a) || !valid.test(b)) return null;
  const scale = Math.max(a.split(".")[1]?.length || 0, b.split(".")[1]?.length || 0);
  const integer = (value: string) => {
    const [whole, fraction = ""] = value.split(".");
    return BigInt(whole + fraction.padEnd(scale, "0"));
  };
  const left = integer(a), right = integer(b);
  return left < right ? -1 : left > right ? 1 : 0;
}
export function validReturnSelection(lines: ReceiptReturnLine[], selection: ReturnLineSelection[]) {
  return selection.length > 0 && new Set(selection.map(row => row.receiptLineId)).size === selection.length &&
    selection.every(row => {
      const line = lines.find(line => line.id === row.receiptLineId);
      return !!line && compareReturnDecimal(row.quantity, "0") === 1 &&
        [0, -1].includes(compareReturnDecimal(row.quantity, line.availableQty) ?? 2);
    });
}
export function returnHasShortage(preview: Pick<ReturnPreview, "lines">) {
  return preview.lines.some(line => line.shortage === true || Number(line.shortage) > 0 || Number(line.afterQuantity) < 0);
}
export function receiptReturnReady(preview: ReturnPreview | undefined, reason: string, photo: string, settlement: "credit" | "cash", cashReturned: boolean, ack: boolean) {
  return !!preview?.token && !!reason.trim() && /^data:image\/(?:jpeg|png|webp);base64,/.test(photo) &&
    (!returnHasShortage(preview) || ack) && (settlement === "credit" || preview.cashAvailable && Number(preview.cashRefundAmount) > 0 && cashReturned);
}
export const supplierReturnHref = (id: string) => `/inventory/returns/${encodeURIComponent(id)}`;
export function supplierReturnBlockedLabel(options: Pick<ReceiptReturns, "blockedCode" | "blockedReason">, ar: boolean) {
  const labels: Record<NonNullable<ReceiptReturns["blockedCode"]>, [string, string]> = {
    missing_permission: ["Supplier return permission is required. Ask Finance to review your access.", "تحتاج صلاحية إرجاع للمورد. اطلب من المالية مراجعة صلاحياتك."],
    not_confirmed: ["Confirm the receipt before returning items to the supplier.", "أكد الاستلام قبل إرجاع الأصناف للمورد."],
    on_hold: ["This receipt is on hold. Resolve the hold before returning items.", "هذا الاستلام معلّق. عالج التعليق قبل إرجاع الأصناف."],
  };
  if (options.blockedCode) return labels[options.blockedCode]?.[ar ? 1 : 0];
  return !ar && options.blockedReason && !/^\d{3}:|[{}]/.test(options.blockedReason) ? options.blockedReason : ar ? "لا توجد كمية متاحة أو ليس لديك صلاحية إرجاع هذا الاستلام." : "No quantity available or you do not have permission to return this receipt.";
}
export const supplierReturnReceiptHref = (id: string, mobile = false) => mobile ? `/m/supplier-receipts/${encodeURIComponent(id)}` : `/supplier-receipts?focus=${encodeURIComponent(id)}`;